import { Injectable } from '@angular/core';
import { CartItem } from './cart.service';
import { PaymentPhase } from '../shared/payment-outcome';

/**
 * A card order that has been placed and is waiting to be paid for.
 *
 * ── Why a snapshot rather than just keeping the cart ─────────────────────────
 * finishOrder() has already closed the order server-side by the time the guest is
 * sent to BORICA, so the live cart MUST be cleared — leaving `currentOrderId` in
 * place would sync later edits into a finished order. But clearing it is what left
 * every failure on an empty screen with a modal and no way back to an order the
 * guest still owes for.
 *
 * So the cart goes and a read-only snapshot stays.
 */
export interface AwaitingPaymentOrder {
  orderId: number;
  shopId: number;
  items: CartItem[];
  total: number;
  note: string;
  currencySymbol: string;

  /*
   * The promo code and its discount, carried across the BORICA round trip.
   *
   * This is the ONLY route they survive by on the card path: the cart is cleared before
   * the redirect, and onPaymentConfirmed() builds the history entry out of this snapshot
   * rather than out of live state. Without them here, a guest who paid by card with a
   * code applied comes back to an order that has forgotten the discount entirely.
   *
   * Optional for the same reason as on OrderHistoryItem: snapshots written before this
   * change are read back from sessionStorage unchanged.
   */
  discountTotal?: number;
  promoCode?: string;
  /** The tip included in `total`; carried across the BORICA round trip like the discount. */
  tipAmount?: number;
  /** The delivery fee included in `total` (0 = free delivery), carried the same way. */
  deliveryFee?: number;

  /**
   * Restored into sessionStorage on the way back.
   *
   * BORICA's return URL carries no table or location params, so Splash re-derives
   * orderingType as 'off-site' and OVERWRITES the stored value — which sends a
   * dine-in guest to the delivery form and gates them on ft_orders_from_home.
   */
  orderingType: string;
  tableContext: Record<string, string> | null;

  /**
   * Carried because `deliveryFormData` is a component field and does not survive the
   * round trip — it is null in exactly the resumed component where "pay again" lives.
   * An off-site PICKUP order sends no billing block either, so without these the
   * retry's M_INFO goes out with a cardholder name and nothing else, and the backend
   * logs that the issuer may challenge or decline. The retry would be likelier to
   * fail than the attempt it follows.
   *
   * No email: the web flow never collects one. The backend falls back to whatever the
   * Woo billing block holds, which is the only source there has ever been.
   */
  customerPhone?: string;

  /** When the order was placed. Display only — it gates nothing. */
  placedAt: number;

  /**
   * The TRANSACTION's created_at, echoed back by /pay.
   *
   * ⚠️ This, never "when we last called /pay". The server's 15-minute reuse window
   * runs from the row, and a resume returns the ORIGINAL row — so a client timing
   * from its own last call drifts ahead and can mint a second live transaction for
   * one order. See withinResumeWindow().
   */
  txCreatedAt?: number;
  transactionId?: number;

  /**
   * When the backend last told us this was declined.
   *
   * ⚠️ Legacy, and deliberately no longer written. It marked THAT a decline happened,
   * never WHEN the clock started — the 60-minute payable window is measured from
   * `txCreatedAt`, because that is what the sweep selects on. Reading this as the
   * clock is the exact mistake isDeclineStillPayable() takes the whole record to
   * prevent, so it is kept only so records written before that fix still parse.
   */
  declinedAt?: number;

  /**
   * Set once the backend has given a TERMINAL answer, with the phase it gave.
   *
   * Not a phase itself: it stops later boots from re-polling a dead order forever,
   * while still rendering the items and the explanation. The first draft deleted the
   * record on a terminal answer instead, which reproduced the empty-screen bug for
   * abandonment — the very case the feature exists for.
   */
  settled?: { phase: PaymentPhase; messageKey: string; at: number };

  /** Consecutive 404s from /payment-status, so a permanent one has a terminus. */
  notFoundCount?: number;
}

/** A settled record is garbage-collected a day later; nothing is actionable by then. */
const SETTLED_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * The outstanding-payment record, in localStorage.
 *
 * ⚠️ localStorage, not sessionStorage. The guest leaves this origin entirely for
 * BORICA's page and comes back through a fresh app boot, and some issuers' ACS pages
 * open a new tab — sessionStorage is gone in both cases. Same reasoning the venue-push
 * stash already documents.
 *
 * ⚠️ Every access is wrapped. Storage can be denied outright (private mode, a browser
 * set to block site data) and quota can be exhausted; a throw here would take down the
 * order screen for a guest who is merely unable to be helped by it.
 */
@Injectable({ providedIn: 'root' })
export class AwaitingPaymentStore {
  /**
   * Keyed per shop so a record can never render against the wrong venue — a guest
   * carrying an unpaid order from one venue scans another's QR routinely.
   */
  private key(shopId: number): string {
    return `awaitingPayment_${shopId}`;
  }

  read(shopId: number): AwaitingPaymentOrder | null {
    let raw: string | null = null;

    try {
      raw = localStorage.getItem(this.key(shopId));
    } catch {
      return null;
    }

    if (!raw) {
      return null;
    }

    let record: AwaitingPaymentOrder;
    try {
      record = JSON.parse(raw) as AwaitingPaymentOrder;
    } catch {
      // Corrupt entry — drop it rather than throwing on every boot forever.
      this.clear(shopId);
      return null;
    }

    if (!record || record.shopId !== shopId || !record.orderId) {
      return null;
    }

    /*
     * The ONLY age-based deletion in this class, and it is deliberately narrow: a
     * record the backend has already settled, a full day later.
     *
     * An UNSETTLED record is never dropped for being old, however old it is. Age says
     * nothing about whether BORICA approved the payment, and a record discarded on a
     * timer takes with it the guest's only evidence of an order they may have been
     * charged for.
     */
    if (record.settled && Date.now() - record.settled.at > SETTLED_TTL_MS) {
      this.clear(shopId);
      return null;
    }

    return record;
  }

  write(record: AwaitingPaymentOrder): void {
    try {
      localStorage.setItem(this.key(record.shopId), JSON.stringify(record));
    } catch {
      // A denied or full localStorage costs the guest the recovery screen, not the
      // order — the order is held server-side and the sweep still resolves it.
    }
  }

  /** Merge fields into an existing record; a no-op when there is none. */
  patch(shopId: number, fields: Partial<AwaitingPaymentOrder>): AwaitingPaymentOrder | null {
    const record = this.read(shopId);
    if (!record) {
      return null;
    }

    const updated = { ...record, ...fields };
    this.write(updated);

    return updated;
  }

  /** Record the backend's terminal answer. Keeps the record; stops the polling. */
  settle(shopId: number, phase: PaymentPhase, messageKey: string): AwaitingPaymentOrder | null {
    return this.patch(shopId, { settled: { phase, messageKey, at: Date.now() } });
  }

  clear(shopId: number): void {
    try {
      localStorage.removeItem(this.key(shopId));
    } catch {
      /* best effort */
    }
  }

  /**
   * Every outstanding record, across venues.
   *
   * SplashComponent calls localStorage.clear() whenever the shop id changes, which
   * would take these with it — and the order behind one stays payable regardless of
   * which QR the guest most recently scanned. Splash reads these first and restores
   * them after the wipe.
   */
  snapshot(): AwaitingPaymentOrder[] {
    const records: AwaitingPaymentOrder[] = [];

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith('awaitingPayment_')) {
          continue;
        }

        const raw = localStorage.getItem(key);
        if (!raw) {
          continue;
        }

        try {
          records.push(JSON.parse(raw) as AwaitingPaymentOrder);
        } catch {
          /* skip a corrupt entry rather than losing the rest */
        }
      }
    } catch {
      return [];
    }

    return records;
  }

  restore(records: AwaitingPaymentOrder[]): void {
    (records || []).forEach(record => {
      if (record && record.shopId && record.orderId) {
        this.write(record);
      }
    });
  }
}
